import { useEffect, useRef, useState } from 'react';

import { useAddToast } from '../../../contexts/ToastContext';
import { getErrorMessage } from '../../../ReactQuery';
import { hardRedirectTo } from '../../../utils';
import Button from '../../generic/Button';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import PasswordInput from '../../generic/Forms/PasswordInput';
import ForgotPasswordLink from '../components/ForgotPasswordLink';
import UseDifferentAccountButton from '../components/UseDifferentAccountButton';
import { useCredentialLogin } from '../hooks/useCredentialLogin';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';
import type { Candidate } from '../../../../shared/types/routes/auth/v2';

import styles from '../Login.module.less';

export type CredentialFormProps = {
  org: Candidate;
  identifier: string;
  showForgotPassword: boolean;
  next?: string;
  onMfaRequired: (mfaMethod: MfaMethodType) => void;
  onBack: () => void;
};

/**
 * The credential step: the user enters their password for the org they picked. The identifier is shown read-only —
 * changing it means going back to the identifier step. The backend resolves the credential against the org's
 * configured method (local password, or LDAP once configured), so this one form serves every credential org.
 */
const CredentialForm = ({ org, identifier, showForgotPassword, next, onMfaRequired, onBack }: CredentialFormProps) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const credentialLogin = useCredentialLogin();
  const addToast = useAddToast();

  // The identifier field is read-only, so land focus on the password instead.
  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  // Advance the flow from an effect once login succeeds, rather than the mutate `onSuccess` callback. React Query runs
  // that callback before notifying its subscribers, so navigating there unmounts this form while React Query is still
  // about to update it — warning about a state update on an unmounted component under React 17. Reacting to the
  // settled `data` defers the transition until after this form has re-rendered. (The error path stays inline below
  // since it doesn't unmount the form.)
  useEffect(() => {
    const response = credentialLogin.data;
    if (!response) {
      return;
    }
    if (response.status === 'mfa_required') {
      onMfaRequired(response.mfaMethod);
      return;
    }
    hardRedirectTo(response.to ?? '/');
  }, [credentialLogin.data, onMfaRequired]);

  const handleSubmit = () => {
    if (!password || credentialLogin.isLoading) {
      return;
    }
    setError(null);
    credentialLogin.mutate(
      { identifier, password, organizationId: org.organizationId, ...(next ? { next } : {}) },
      {
        onError: requestError => {
          // Bad credentials (400) and an SSO-required org (403) are expected outcomes shown inline; anything else
          // (network failure, 5xx) is unexpected and surfaces as a toast.
          if (requestError instanceof Response && requestError.status < 500) {
            setError(getErrorMessage(requestError, 'Invalid username or password.'));
            return;
          }
          addToast({ type: 'error', content: getErrorMessage(requestError, 'Something went wrong. Please try again.') });
        },
      },
    );
  };

  return (
    <form
      className={styles.card}
      onSubmit={event => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <h1 className={styles.heading}>Sign in to {org.organizationName}</h1>
      <div className={styles.fieldGroup}>
        <LabeledTextInput label="Email or username" name="identifier" value={identifier} isReadOnly />
        <PasswordInput ref={passwordRef} label="Password" value={password} onChange={setPassword} isRequired />
        {showForgotPassword && <ForgotPasswordLink />}
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      {/* A submit button lets Enter submit the form; the login runs in the form's onSubmit, so onClick is unused. */}
      <Button isFullWidth size="large" isSubmitButton isDisabled={credentialLogin.isLoading} onClick={() => undefined}>
        Sign in
      </Button>
      <UseDifferentAccountButton onClick={onBack} />
    </form>
  );
};

export default CredentialForm;
